import React from 'react';
import { useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';

interface PathMarkerDiamondIconProps {
  /** Letter displayed in the centre of the diamond. */
  letter?: string;
  /** Overall pixel size of the square bounding box. Defaults to 20. */
  size?: number;
  /** Diamond fill colour. Defaults to chart blue. */
  fillColor?: string;
  /**
   * Colour used for the polygon border stroke and centre letter.
   * Defaults to background.paper (white in light/dark mode) so the letter
   * contrasts against the default chart-blue fill.
   */
  letterColor?: string;
  sx?: SxProps<Theme>;
}

/**
 * Inline SVG diamond icon that matches the PathMarkerDiamond used in the path
 * builder canvas — a rotated square (not a gemstone shape).
 */
const PathMarkerDiamondIcon: React.FC<PathMarkerDiamondIconProps> = ({ letter, size = 20, fillColor, letterColor, sx }) => {
  const theme = useTheme();
  const fill = fillColor ?? theme.vars!.palette.chart.blue;
  const stroke = letterColor ?? theme.vars!.palette.background.paper;
  const c = size / 2;
  const half = c * 0.72; // slightly inset from edges for stroke room
  const points = `${c},${c - half} ${c + half},${c} ${c},${c + half} ${c - half},${c}`;

  return (
    <Box
      component="svg"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      sx={{ flexShrink: 0, display: 'block', ...sx }}
    >
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={1} />
      {letter && (
        <text
          x={c}
          y={c}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={size * 0.42}
          fontFamily="inherit"
          fill={stroke}
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {letter}
        </text>
      )}
    </Box>
  );
};

export default PathMarkerDiamondIcon;
